import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {of, tap } from 'rxjs';
import { switchMap, map, catchError, withLatestFrom, filter } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import {
  addGlobalConfig,
  addGlobalConfigFailure,
  addGlobalConfigSuccess, addReportColumn, addStockSize, changeReportColumnPriority,
  changeStockSizePriority,
  loadGlobalConfigs,
  loadGlobalConfigsFailure,
  loadGlobalConfigsSuccess,
  modifyGlobalConfig,
  modifyGlobalConfigFailure,
  modifyGlobalConfigSuccess,
  removeGlobalConfig,
  removeGlobalConfigFailure,
  removeGlobalConfigSuccess, removeReportColumn,
  removeSearchConditions,
  removeSearchParameters,
  removeStockSize,
  modifyProcessPriorities,
  modifyProcessPrioritiesSuccess,
  modifyProcessPrioritiesFailure,
  removeProcessAndRecalculatePriorities,
  setHighlightId
} from "./global-config.action";
import {AppState} from "../../../shared/state/app.state";
import {GlobalConfigService} from "../../data-access/global-config.service";
import {selectGlobalConfigs} from "./global-config.selectors";
import {GlobalConfig} from "../../models/global-config.models";
import {
  addBuildKitFailure,
  addBuildKitSuccess, addCadModelRevisionQuantityFailure,
  addCadModelRevisionQuantitySuccess, addTravelerFailure,
  addTravelerSuccess, loadBuildKitsFailure, loadCadModelsFailure, loadTravelerCategoriesFailure,
  modifyBuildKitFailure, modifyBuildKitSettingFailure,
  modifyBuildKitSettingSuccess,
  modifyBuildKitSuccess, modifyCadModelRevisionQuantityFailure,
  modifyCadModelRevisionQuantitySuccess, modifyTravelerFailure,
  modifyTravelerSuccess, removeBuildKitFailure,
  removeBuildKitSuccess, removeCadModelRevisionQuantityFailure,
  removeCadModelRevisionQuantitySuccess, removeTravelerFailure,
  removeTravelerSuccess
} from "../../../build-kit/state/build-kit/build-kit.action";
import {ToastMessageOptions, MessageService} from "primeng/api";
import {GetMessage} from "../../utils/toast-messages.utils";


@Injectable()
export class GlobalConfigEffects {
  loadGlobalConfigs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadGlobalConfigs),
      withLatestFrom(this.store.select(selectGlobalConfigs)),
      switchMap(([action, globalConfigState]) =>
        this.globalConfigService.get(globalConfigState.globalConfigService)
          .pipe(
            map((globalConfigs) => loadGlobalConfigsSuccess({globalConfigs: globalConfigs})),
            catchError((error) => of(loadGlobalConfigsFailure({ error: GetMessage('LoadFailure', globalConfigState.globalConfigLabel) })))
          )
      )
    )
  );

  changeStockSizePriority$ = createEffect(() =>
    this.actions$.pipe(
      ofType(changeStockSizePriority),
      withLatestFrom(this.store.select(selectGlobalConfigs)),
      switchMap(([action, globalConfigState]) => {
        return this.globalConfigService.update(globalConfigState.globalConfigService, {
            ...globalConfigState.globalConfigs.find(x => x.id == action.globalConfigId),
            id: action.globalConfigId,
            stockSizes: action.stockSize.map(x => x.stockSize),
          } as any)
            .pipe(
              map((globalConfig) => modifyGlobalConfigSuccess({globalConfigs: globalConfig, successMessage: GetMessage('ModifySuccess', 'Stock Size Priority')})),
              catchError((error) => of(modifyGlobalConfigFailure({error: GetMessage('ModifyFailure', 'Stock Size Priority')})))
            )
        }
      )
    )
  );

  changeReportColumnPriority$ = createEffect(() =>
    this.actions$.pipe(
      ofType(changeReportColumnPriority),
      withLatestFrom(this.store.select(selectGlobalConfigs)),
      switchMap(([action, globalConfigs]) =>
        this.globalConfigService.update(globalConfigs.globalConfigService, {
          ...globalConfigs.globalConfigs.find(x => x.id == action.globalConfigId),
          id: action.globalConfigId,
          reportColumns: action.reportColumns,

        } as any)
          .pipe(
            map((globalConfig) => modifyGlobalConfigSuccess({globalConfigs: globalConfig, successMessage: GetMessage('ModifySuccess', 'Report Column Priority')})),
            catchError((error) => of(modifyGlobalConfigFailure({ error: GetMessage('ModifyFailure', 'Report Column Priority') })))
          )
      )
    )
  );

  addStockSize$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addStockSize),
      withLatestFrom(this.store.select(selectGlobalConfigs)),
      switchMap(([action, globalConfigs]) => {
          const configToUpdate = globalConfigs.globalConfigs.find(config => config.id === action.globalConfigId) as any;
          return this.globalConfigService.update(globalConfigs.globalConfigService, {
            ...globalConfigs.globalConfigs.find(x => x.id == action.globalConfigId),
            id: action.globalConfigId,
            stockSizes: configToUpdate.stockSizes.map(x => x.stockSize)
          } as any)
            .pipe(
              map((globalConfig) => modifyGlobalConfigSuccess({globalConfigs: globalConfig, successMessage: GetMessage('AddSuccess', 'Stock Size')})),
              catchError((error) => of(modifyGlobalConfigFailure({error: GetMessage('AddFailure', 'Stock Size')})))
            )
        }
      )
    )
  );

  addReportColumn$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addReportColumn),
      withLatestFrom(this.store.select(selectGlobalConfigs)),
      switchMap(([action, globalConfigState]) => {
          const configToUpdate = globalConfigState.globalConfigs.find(config => config.id === action.globalConfigId) as any;
          return this.globalConfigService.update(globalConfigState.globalConfigService, {
            ...globalConfigState.globalConfigs.find(x => x.id == action.globalConfigId),
            id: action.globalConfigId,
            reportColumns: configToUpdate.reportColumns
          } as any)
            .pipe(
              map((globalConfig) => modifyGlobalConfigSuccess({globalConfigs: globalConfig, successMessage: GetMessage('AddSuccess', 'Report Column')})),
              catchError((error) => of(modifyGlobalConfigFailure({error: GetMessage('AddFailure', 'Report Column')})))
            )
        }
      )
    )
  );

  modifyGlobalConfigs$ = createEffect(() =>
      this.actions$.pipe(
        ofType(modifyGlobalConfig),
        withLatestFrom(this.store.select(selectGlobalConfigs)),
        switchMap(([action, globalConfigState]) => {
            const payload = {
              ...action.globalConfig,
              stockSizes: (action.globalConfig as any).stockSizes?.map(x => x.stockSize) ?? null
            };

            return this.globalConfigService.update(globalConfigState.globalConfigService, payload)
              .pipe(
                map((globalConfig) => modifyGlobalConfigSuccess({globalConfigs: globalConfig, successMessage: GetMessage('ModifySuccess', globalConfigState.globalConfigLabel)})),
                catchError((error) => of(modifyGlobalConfigFailure({error: GetMessage('ModifyFailure', globalConfigState.globalConfigLabel)})))
              )
          }
        )
      )
  );

  // New effect for modifying process priorities
  modifyProcessPriorities$ = createEffect(() =>
    this.actions$.pipe(
      ofType(modifyProcessPriorities),
      withLatestFrom(this.store.select(selectGlobalConfigs)),
      switchMap(([action, globalConfigState]) => {
        
        const prioritiesPayload = action.priorities
          .map(p => ({
            id: Number(p.id),
            priority: p.priority
          }));

        return this.globalConfigService
        .updatePriorities(globalConfigState.globalConfigService, prioritiesPayload)
          .pipe(
            switchMap(() => [
              modifyProcessPrioritiesSuccess({ 
                successMessage: GetMessage('ModifySuccess', 'Process Priorities') 
              }),
            ]),
            catchError((error) => of(modifyProcessPrioritiesFailure({ error: GetMessage('ModifyFailure', 'Process Priorities') })))
          )
      } )
    )
  );

  removeProcessAndRecalc$ = createEffect(() =>
    this.actions$.pipe(
      ofType(removeProcessAndRecalculatePriorities),
      withLatestFrom(this.store.select(selectGlobalConfigs)),
      switchMap(([{ globalConfigId }, globalConfigState]) => {
        const priorities = [...globalConfigState.globalConfigs]
          .filter(p => String(p.id) !== String(globalConfigId))
          .sort((a: any, b: any) => a.priority - b.priority)
          .map((p, i) => ({ id: Number(p.id), priority: i + 1 }));

        return [
          removeGlobalConfig({ globalConfigId }),
          modifyProcessPriorities({ priorities }),
        ];
      })
    )
  );

  removeStockSize$ = createEffect(() =>
    this.actions$.pipe(
      ofType(removeStockSize),
      withLatestFrom(this.store.select(selectGlobalConfigs)),
      switchMap(([action, globalConfigs]) => {
          const updatedStockSize = globalConfigs.globalConfigs.find(cg => cg.id === action.globalConfigId) as any;
          return this.globalConfigService.update(globalConfigs.globalConfigService, {
            ...globalConfigs.globalConfigs.find(x => x.id == action.globalConfigId),
            id: updatedStockSize.id,
            stockSizes: updatedStockSize.stockSizes.map(x => x.stockSize)
          } as any)
            .pipe(
              map((globalConfigs) => modifyGlobalConfigSuccess({globalConfigs: globalConfigs, successMessage: GetMessage('RemoveSuccess', 'Stock Size')})),
              catchError((error) => of(modifyGlobalConfigFailure({error: GetMessage('RemoveFailure', 'Stock Size')})))
            )
        }
      )
    )
  );

  removeReportColumn$ = createEffect(() =>
    this.actions$.pipe(
      ofType(removeReportColumn),
      withLatestFrom(this.store.select(selectGlobalConfigs)),
      switchMap(([action, globalConfigs]) => {
          const updatedReportColumns = globalConfigs.globalConfigs.find(cg => cg.id === action.globalConfigId) as any;
          return this.globalConfigService.update(globalConfigs.globalConfigService, {
            ...globalConfigs.globalConfigs.find(x => x.id == action.globalConfigId),
            id: updatedReportColumns.id,
            reportColumns: updatedReportColumns.reportColumns
          } as any)
            .pipe(
              map((globalConfigs) => modifyGlobalConfigSuccess({globalConfigs: globalConfigs, successMessage: GetMessage('RemoveSuccess', 'Report Column')})),
              catchError((error) => of(modifyGlobalConfigFailure({error: GetMessage('RemoveFailure', 'Report Column')})))
            )
        }
      )
    )
  );

  removeSearchParameter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(removeSearchParameters),
      withLatestFrom(this.store.select(selectGlobalConfigs)),
      switchMap(([action, globalConfigs]) => {
        const updatedGlobalConfiguration = globalConfigs.globalConfigs.find(mf => mf.id === action.globalConfigId) as any;
          return this.globalConfigService.update(globalConfigs.globalConfigService, {
            ...updatedGlobalConfiguration,
            stockSizes: updatedGlobalConfiguration.stockSizes?.map(x => x.stockSize)
          })
            .pipe(
              map((globalConfigs) => modifyGlobalConfigSuccess({globalConfigs: globalConfigs, successMessage: GetMessage('RemoveSuccess', 'Search Parameter')})),
              catchError((error) => of(modifyGlobalConfigFailure({error: GetMessage('RemoveFailure', 'Search Parameter')})))
            )
        }
      )
    )
  );

  removeSearchCondition$ = createEffect(() =>
    this.actions$.pipe(
      ofType(removeSearchConditions),
      withLatestFrom(this.store.select(selectGlobalConfigs)),
      switchMap(([action, globalConfigs]) => {
          const updatedGlobalConfiguration = globalConfigs.globalConfigs.find(mf => mf.id === action.globalConfigId) as any;
          return this.globalConfigService.update(globalConfigs.globalConfigService, {
            ...updatedGlobalConfiguration,
            stockSizes: updatedGlobalConfiguration.stockSizes?.map(x => x.stockSize)
          })
            .pipe(
              map((globalConfigs) => modifyGlobalConfigSuccess({globalConfigs: globalConfigs, successMessage: GetMessage('RemoveSuccess', 'Search Condition')})),
              catchError((error) => of(modifyGlobalConfigFailure({error: GetMessage('RemoveFailure', 'Search Condition')})))
            )
        }
      )
    )
  );

  createGlobalConfiguration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addGlobalConfig),
      withLatestFrom(this.store.select(selectGlobalConfigs)),
      switchMap(([action, globalConfigState]) =>
        this.globalConfigService.create(globalConfigState.globalConfigService, action.globalConfig)
          .pipe(
            map((addedGlobalConfiguration) => addGlobalConfigSuccess({globalConfig: addedGlobalConfiguration, successMessage: GetMessage('AddSuccess', globalConfigState.globalConfigLabel)})),
            catchError((error) => of(addGlobalConfigFailure({ error: GetMessage('AddFailure', globalConfigState.globalConfigLabel) })))
          )
      )
    )
  );

  highlightOnAddConfig$ = createEffect(() =>
    this.actions$.pipe(
       ofType(addGlobalConfigSuccess),
      map(({ globalConfig }) => setHighlightId({ id: Number(globalConfig.id) }))
    )
  );

  removeGlobalConfiguration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(removeGlobalConfig),
      withLatestFrom(this.store.select(selectGlobalConfigs)),
      switchMap(([action, globalConfigState]) =>
        this.globalConfigService.remove(globalConfigState.globalConfigService, action.globalConfigId)
          .pipe(
            map((removedGlobalConfigurationId) => removeGlobalConfigSuccess({globalConfigId: removedGlobalConfigurationId, successMessage: GetMessage('RemoveSuccess', globalConfigState.globalConfigLabel)})),
            catchError((error) => of(removeGlobalConfigFailure({ error: GetMessage('RemoveFailure', globalConfigState.globalConfigLabel) })))
          )
      )
    )
  );

  toastSuccessMessages$ = createEffect(() =>
      this.actions$.pipe(
        ofType(
          removeGlobalConfigSuccess,
          addGlobalConfigSuccess,
          modifyGlobalConfigSuccess,
          modifyProcessPrioritiesSuccess
        ),
        tap(({ successMessage }) => {
            return this.messageService.add({
              severity: 'success',
              summary: successMessage,
            } as ToastMessageOptions)
          }
        )
      ),
    { dispatch: false }
  );

  toastErrorMessages$ = createEffect(() =>
      this.actions$.pipe(
        ofType(
          removeGlobalConfigFailure,
          addGlobalConfigFailure,
          modifyGlobalConfigFailure,
          loadGlobalConfigsFailure,
          modifyProcessPrioritiesFailure
        ),
        tap(({ error }) => {
            return this.messageService.add({
              severity: 'error',
              summary: error,
            } as ToastMessageOptions)
          }
        )
      ),
    { dispatch: false }
  );


    // private globalConfigService: GlobalConfigurationService
  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private globalConfigService: GlobalConfigService,
    private messageService: MessageService,
  ) {}
}
